import { falAward } from 'shared/@generated/icons/falAward';
import { falCat } from 'shared/@generated/icons/falCat';
import { falDna } from 'shared/@generated/icons/falDna';
import { falDog } from 'shared/@generated/icons/falDog';
import { View } from 'react-native';
import { Text } from 'shared/components/text';
import { useTranslation } from 'react-i18next';
import { sortOptions, useBreedsFilterStore } from 'shared/state/breeds-filter';
import { TextInput } from 'shared/components/form/text-input';
import { Animal, BreedType, Characteristic } from 'shared/@generated/models';
import { CharacteristicBadge } from 'shared/components/characteristic-badge';
import { Switch } from 'shared/components/form/switch';
import { ExpandableSelect } from 'shared/components/form/expandable-select';

import React from 'react';

export const BreedsFilterForm = () => {
  const { t } = useTranslation('components');
  const filter = useBreedsFilterStore((state) => state.filter);
  const setFilter = useBreedsFilterStore((state) => state.setFilter);

  return (
    <View className={'gap-6'}>
      <View className={'gap-1.5'}>
        <TextInput
          placeholder={t('breeds_filter_form.search_by_name')}
          value={filter.query ?? ''}
          onChangeText={(val: string) => setFilter({ query: val })}
          dense={true}
        />

        <ExpandableSelect
          title={t('breeds_filter_form.animal')}
          options={[
            { label: t('breeds_filter_form.all_animals'), value: null },
            ...Object.entries(Animal).map(([_key, value]) => ({
              label: t(`common:enums.animal.${value}`),
              value,
              icon: { [Animal.Dog]: falDog, [Animal.Cat]: falCat }[value],
            })),
          ]}
          value={filter.animal}
          onChange={(animal) => setFilter({ animal })}
        />

        <ExpandableSelect
          title={t('breeds_filter_form.breed_type')}
          options={[
            {
              label: t('breeds_filter_form.all_breed_types'),
              value: null,
            },
            ...Object.entries(BreedType)
              .filter(([_key, val]) => val !== BreedType.MixedBreed)
              .map(([_key, value]) => ({
                label: t(`common:enums.breed_type.${value}`),
                value,
                icon: {
                  [BreedType.Purebred]: falAward,
                  [BreedType.Crossbreed]: falDna,
                  [BreedType.MixedBreed]: undefined,
                }[value],
              })),
          ]}
          value={filter.breedType}
          onChange={(breedType) => setFilter({ breedType })}
        />

        <ExpandableSelect
          title={t('breeds_filter_form.size')}
          options={[
            {
              label: t('breeds_filter_form.all_sizes'),
              value: null,
            },
            ...Object.entries(Characteristic)
              .filter(([key]) => key.startsWith('Size'))
              .map(([_, value]) => ({
                label: t(`breed_badge.${value}`),
                value,
              })),
          ]}
          value={filter.size}
          onChange={(size) => setFilter({ size })}
        />

        <ExpandableSelect
          title={t('breeds_filter_form.sort_by')}
          options={[
            {
              label: t('breeds_filter_form.sort.recommended'),
              value: null,
            },
            ...sortOptions.map((value) => ({
              label: t(`breeds_filter_form.sort.${value}`),
              value,
            })),
          ]}
          value={filter.sortBy}
          onChange={(sortBy) => setFilter({ sortBy })}
        />
      </View>

      <View className={'gap-1.5'}>
        <Text className={'text-brand-brown-dark font-black'}>
          {t('breeds_filter_form.characteristics')}
        </Text>

        {Object.entries(Characteristic)
          .filter(([key]) => !key.startsWith('Size'))
          .map(([key, value]) => (
            <CharacteristicSwitch key={key} characteristic={value} />
          ))}
      </View>
    </View>
  );
};

const CharacteristicSwitch = ({ characteristic }: { characteristic: Characteristic }) => {
  const filter = useBreedsFilterStore((state) => state.filter);
  const setFilter = useBreedsFilterStore((state) => state.setFilter);
  const isSelected = filter.characteristics?.includes(characteristic) ?? false;

  const onPress = () => {
    if (isSelected) {
      setFilter({
        characteristics: filter.characteristics?.filter((c) => c !== characteristic) ?? [],
      });
    } else {
      setFilter({ characteristics: [...(filter.characteristics ?? []), characteristic] });
    }
  };

  return (
    <View className={'border-brand-brown-dark/10 flex-row items-center border-b px-3 py-2'}>
      <View className={'flex-1 origin-left scale-90'}>
        <CharacteristicBadge characteristic={characteristic} />
      </View>

      <Switch value={isSelected} onPress={onPress} />
    </View>
  );
};
